import type { TFunction } from 'i18next'
import type { TaskReminder } from '@/src/renderer/lib/db/models'
import { isStandardReminder } from '@/src/renderer/lib/db/models'
import type { ReminderSilence } from '@/src/renderer/lib/reminders/schedule'
import { shortRecurrenceLabel } from '@/src/renderer/lib/recurrence/shortRecurrenceLabel'
import { parseDateKey } from '@/src/renderer/lib/date/calendar'

/*
  **Wie eine Erinnerung heißt** (Plan task-reminders, Etappe 5) — an der Liste im Popover, am Chip
  im Kopf der Aufgaben-Details, im Aufgaben-Menü. Die Wörter stehen unter `reminders.*`.
*/

const sameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()

const clock = (date: Date, locale: string) =>
  new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit' }).format(date)

/** Ein Vorlauf in Worten: „15 Min.“, „1 Std.“, „1 Std. 30 Min.“, „2 Tage“. */
export function formatReminderOffset(minutes: number, t: TFunction): string {
  if (minutes >= 1440 && minutes % 1440 === 0) return t('reminders.offset.days', { count: minutes / 1440 })
  if (minutes >= 60 && minutes % 60 === 0) return t('reminders.offset.hours', { count: minutes / 60 })
  if (minutes > 60) return t('reminders.offset.hoursMinutes', { hours: Math.floor(minutes / 60), minutes: minutes % 60 })
  return t('reminders.offset.minutes', { count: minutes })
}

/** Ein Zeitpunkt in Worten: „Heute, 13:30“, „Morgen, 09:00“, „Mo., 6.10., 14:00“. */
export function reminderWhenLabel(date: Date, now: Date, t: TFunction, locale: string): string {
  const time = clock(date, locale)
  if (sameDay(date, now)) return t('reminders.when.today', { time })
  const tomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1)
  if (sameDay(date, tomorrow)) return t('reminders.when.tomorrow', { time })
  const day = new Intl.DateTimeFormat(locale, { weekday: 'short', day: 'numeric', month: 'numeric' }).format(date)
  return t('reminders.when.date', { date: day, time })
}

/** „30 Min. vorher“, „Zur Uhrzeit“, „1 Std. vor dem Ende“ — für einen Vorlauf und einen Bezug. */
export function relativeReminderLabel(anchor: 'start' | 'deadline', offsetMinutes: number, t: TFunction): string {
  if (offsetMinutes === 0) return t(anchor === 'start' ? 'reminders.label.atStart' : 'reminders.label.atDeadline')
  const offset = formatReminderOffset(offsetMinutes, t)
  return t(anchor === 'start' ? 'reminders.label.before' : 'reminders.label.beforeDeadline', { offset })
}

/**
 * **Was die Erinnerung ist** — nicht, wann sie klingelt: „30 Min. vorher“, „Standard · 30 Min.
 * vorher“, „1 Std. vor dem Ende“, „Mo., 6.10., 14:00“, „Täglich 15:00“.
 */
export function reminderLabel(reminder: TaskReminder, defaultOffset: number | null, now: Date, t: TFunction, locale: string): string {
  if (reminder.kind === 'relative') {
    const anchor = reminder.anchor ?? 'start'
    if (!isStandardReminder(reminder)) return relativeReminderLabel(anchor, reminder.offsetMinutes ?? 0, t)
    const inner = defaultOffset === null ? t('reminders.label.off') : relativeReminderLabel(anchor, defaultOffset, t)
    return t('reminders.label.standard', { label: inner })
  }
  if (!reminder.remindDate || !reminder.remindTime) return t('reminders.label.fallback')
  const [h, m] = reminder.remindTime.split(':').map(Number)
  const day = parseDateKey(reminder.remindDate)
  const at = new Date(day.getFullYear(), day.getMonth(), day.getDate(), h ?? 0, m ?? 0)
  if (reminder.recurrence) return t('reminders.label.rhythm', { rhythm: shortRecurrenceLabel(reminder.recurrence, t, locale), time: clock(at, locale) })
  return reminderWhenLabel(at, now, t, locale)
}

/** Warum sie still ist: „Start hat keine Uhrzeit“, „Aufgabe erledigt“ … */
export const silenceLabel = (reason: ReminderSilence, t: TFunction): string => t(`reminders.silence.${reason}`)

/**
 * **Die Zeile rechts neben der Beschriftung**: wann sie als Nächstes klingelt, oder warum nicht.
 * `next` und `silence` kommen aus dem Zeitplan (`nextReminderAt`, `reminderSilence`).
 */
export function reminderStatusLabel(
  { next, silence, snoozed }: { next: Date | null; silence: ReminderSilence | null; snoozed: boolean },
  now: Date, t: TFunction, locale: string,
): { text: string; silent: boolean } {
  if (silence) return { text: silenceLabel(silence, t), silent: true }
  if (!next) return { text: t('reminders.status.past'), silent: true }
  const when = reminderWhenLabel(next, now, t, locale)
  return { text: snoozed ? t('reminders.status.snoozed', { when }) : when, silent: false }
}
